import React from 'react'
import { Image, ImageStyle, View } from 'react-native'
import { useTheme } from '@ui-kitten/components'
import moment from 'moment'
import { StyleService, useStyleSheet } from '@src/style/service'
import { useIsDarkMode } from '@src/config/theme'
import { cgmSteloImage } from '@src/assets/images'
import { Button, Icon, Text, IconName } from '../base'

interface ExpirationNotificationBannerProps {
  content: string
  actionButtonLabel: string
  dismissButtonLabel: string
  dismiss: () => void
  action: (() => void) | undefined
  payload: any
  iconName: IconName
  isSteloNotification: boolean
}

export const ExpirationNotificationBanner = ({
  content,
  actionButtonLabel,
  dismissButtonLabel,
  dismiss,
  action,
  payload,
  iconName,
  isSteloNotification,
}: ExpirationNotificationBannerProps) => {
  const styles = useStyleSheet(themedStyles)
  const theme = useTheme()
  const isDarkMode = useIsDarkMode()

  const { expirationDate, maxAmountOfDays } = payload

  if (!expirationDate || !maxAmountOfDays) {
    return null
  }

  const expirationTime = moment(payload.expirationDate)
  const daysRemaining = expirationTime.diff(moment(), 'days')
  const progressBarWidth = ((maxAmountOfDays - daysRemaining) / maxAmountOfDays) * 100
  const remainingText =
    daysRemaining > 0
      ? `Only ${daysRemaining} ${daysRemaining > 1 ? 'days' : 'day'} remaining`
      : 'Expires today'
  const containerBackgroundColor = isDarkMode ? '#ffefd719' : theme['theme.marketing.lightYellow']

  return (
    <View
      style={[
        styles.banner,
        {
          backgroundColor: containerBackgroundColor,
          borderWidth: isDarkMode ? 1 : 0,
          borderColor: theme['theme.warning.lighter'],
        },
      ]}
    >
      <View style={{ flexDirection: 'row' }}>
        {isSteloNotification ? (
          <Image source={cgmSteloImage.imageSource} style={styles.image as ImageStyle} />
        ) : (
          <Icon name={iconName} style={styles.icon} />
        )}
        <View style={{ flex: 1 }}>
          <View style={{ flexDirection: 'row', marginLeft: 'auto', alignItems: 'center' }}>
            <Icon name="warning" weight="fill" style={styles.icon} />
            <Text type="regular" style={{ marginLeft: 0 }}>
              {remainingText}
            </Text>
          </View>
          <View style={styles.progressBarContainer}>
            <View style={[styles.progressBar, { width: `${progressBarWidth}%` }]} />
          </View>
        </View>
      </View>
      <View style={styles.contentWrapper}>
        <Text type="regular" style={styles.content}>
          {content}
        </Text>
      </View>
      <View style={styles.buttonWrapper}>
        <Button
          type="transparent"
          size="small"
          accessibilityLabel={dismissButtonLabel}
          style={styles.button}
          onPress={dismiss}
        >
          {dismissButtonLabel}
        </Button>
        <Button
          type="primary"
          size="small"
          accessibilityLabel={actionButtonLabel}
          style={styles.button}
          onPress={action}
        >
          {actionButtonLabel}
        </Button>
      </View>
    </View>
  )
}

const themedStyles = StyleService.create({
  banner: {
    margin: 16,
    padding: 16,
    borderRadius: 8,
  },
  contentWrapper: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  icon: {
    color: 'theme.warning.darkest',
    marginRight: 6,
  },
  progressBarContainer: {
    position: 'relative',
    borderRadius: 8,
    marginTop: 8,
    marginBottom: 8,
    height: 8,
    width: '100%',
    backgroundColor: 'theme.solid.white',
  },
  progressBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    borderRadius: 8,
    backgroundColor: 'theme.warning.base',
  },
  content: {
    flex: 1,
    color: 'theme.text.primary',
    marginTop: 8,
  },
  buttonWrapper: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  button: {
    marginLeft: 8,
  },
  image: {
    width: 48,
    height: 48,
    marginRight: 16,
  },
})
